/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState } from 'react';
import { Flex, Table, Tag, Transfer, Button } from 'antd';
import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { API_URL } from 'env';

type TransferItem = GetProp<TransferProps, 'dataSource'>[number];
type TableRowSelection<T extends object> = TableProps<T>['rowSelection'];

interface DataType {
    key: string;
    id: string;
    accountName: string;
    login_id: string;
    tag: string;
}

interface TableTransferProps extends TransferProps<TransferItem> {
    dataSource: DataType[];
    leftColumns: TableColumnsType<DataType>;
    rightColumns: TableColumnsType<DataType>;
}

// Customize Table Transfer
const TableTransfer: React.FC<TableTransferProps> = (props) => {
    const { leftColumns, rightColumns, ...restProps } = props;
    return (
        <Transfer style={{ width: '100%' }} {...restProps}>
            {({
                direction,
                filteredItems,
                onItemSelect,
                onItemSelectAll,
                selectedKeys: listSelectedKeys,
                disabled: listDisabled,
            }) => {
                const columns = direction === 'left' ? leftColumns : rightColumns;
                const rowSelection: TableRowSelection<TransferItem> = {
                    getCheckboxProps: () => ({ disabled: listDisabled }),
                    onChange(selectedRowKeys) {
                        onItemSelectAll(selectedRowKeys, 'replace');
                    },
                    selectedRowKeys: listSelectedKeys,
                    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                };

                return (
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={filteredItems}
                        size="small"
                        style={{ pointerEvents: listDisabled ? 'none' : undefined }}
                        onRow={({ key, disabled: itemDisabled }) => ({
                            onClick: () => {
                                if (itemDisabled || listDisabled) {
                                    return;
                                }
                                onItemSelect(key, !listSelectedKeys.includes(key));
                            },
                        })}
                    />
                );
            }}
        </Transfer>
    );
};

export async function AddAccountToUser({ updateMap }: { updateMap: { userId: string, accountId: string }[] }) {
    try {
        for (const { userId, accountId } of Object.values(updateMap)) {
            const response = await fetch(`${API_URL}api/v1/be/trade_account/${accountId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ user_id: userId })
            });
            if (!response.ok) {
                console.log('Failed to update account:', response.statusText);
            }
        }
    } catch (error) {
        console.error('Error updating accounts:', error);
    }
}
export async function RemoveAccountFromUser({ updateMap }: { updateMap: { userId: string, accountId: string }[] }) {
    try {
        for (const { userId, accountId } of Object.values(updateMap)) {
            const response = await fetch(`${API_URL}api/v1/be/trade_account/${accountId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ user_id: null })
            });
            if (!response.ok) {
                console.log('Failed to update account:', response.statusText);
            }
        }
    } catch (error) {
        console.error('Error updating accounts:', error);
    }
}

const columns: TableColumnsType<DataType> = [
    {
        title: 'Account Name',
        dataIndex: 'accountName',
        key: 'accountName',
        width: '40%',
    },
    {
        title: 'Login ID',
        dataIndex: 'login_id',
        key: 'login_id',
        width: '20%',
    },
    {
        title: 'Tag',
        dataIndex: 'tag',
        key: 'tag',
        width: '20%',
        render: (tag) => <Tag color="blue">{tag}</Tag>,
    },

];

const filterOption = (input: string, item: DataType) => item.login_id?.includes(input) || item.tag?.includes(input) || item.accountName?.includes(input);


const EditUserAssignAccountModalContent = ({ userId }: { userId: string }) => {
    const [assignAccountData, setAssignAccountData] = useState<DataType[]>([]);
    const [availableAccounts, setAvailableAccounts] = useState<DataType[]>([]);


    const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>(availableAccounts.map(acc => acc.key));
    const [disabled, setDisabled] = useState(false);
    // fetch available accounts and assigned accounts for the user
    const fetchData = async () => {
        // Simulate fetching data
        const response = await fetch(`${API_URL}api/v1/be/trade_accounts`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const result = await response.json();
        const allAccounts: DataType[] = (result.data?.items || []).map((item: any) => ({
            key: String(item.id),
            id: String(item.user_id),
            login_id: item.account_number || 'Unknown Login ID',
            accountName: item.nickname || 'Unknown Name',
            tag: item.platform || '',
        }));
        const assignedAccounts = allAccounts.filter(account => String(account.id) === userId);
        const unassignedAccounts = allAccounts.filter(account => String(account.id) === 'null');

        setAssignAccountData(assignedAccounts);
        setAvailableAccounts(unassignedAccounts);
        setTargetKeys(unassignedAccounts.map(acc => acc.key));
    };
    React.useEffect(() => {
        fetchData();
    }, [userId]);


    const onChange: TableTransferProps['onChange'] = (nextTargetKeys, direction, moveKeys) => {
        setTargetKeys(nextTargetKeys);
    };

    const toggleDisabled = (checked: boolean) => {
        setDisabled(checked);
    };

    return (
        <div className="p-4">
            <Flex align="start" gap="middle" vertical>
                <TableTransfer
                    dataSource={[...assignAccountData, ...availableAccounts]}
                    targetKeys={targetKeys}
                    onChange={onChange}
                    render={item => item.id}
                    showSearch
                    titles={['Assigned Accounts', 'Available Accounts']}
                    filterOption={filterOption}
                    leftColumns={columns}
                    rightColumns={columns}
                    disabled={disabled}
                />
                <div className='w-full flex flex-row justify-end'>
                    <Button
                        type="primary"
                        onClick={async () => {
                            const addedAccount = availableAccounts.filter(item => !(targetKeys ?? []).includes(item.key)).map(item => item.key);
                            const removedAccount = assignAccountData.filter(item => (targetKeys ?? []).includes(item.key)).map(item => item.key);
                            const addToAccount = await AddAccountToUser({ updateMap: addedAccount.map(key => ({ userId, accountId: key })) });
                            const removeFromAccount = await RemoveAccountFromUser({ updateMap: removedAccount.map(key => ({ userId, accountId: key })) });
                            fetchData();
                        }}
                        disabled={disabled}
                    >
                        Save Changes
                    </Button>
                </div>
            </Flex>
        </div>
    )
}

export default EditUserAssignAccountModalContent;